import React, { useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText, Tabs, Tab, Box, Typography } from "@mui/material";
import { api } from "../../utils/api";
import CustomSnackbar from "../../components/CustomSnackbar";
import {
  findPricingRowIndex,
  shouldPersistPricingRow,
  shouldSendPriceInRequest,
  shouldSendVehicleTierFieldsInRequest,
  variantsObjectHasAny,
} from "./pricingPersistLogic";

const MerchantStorePricing = () => {
  const [merchantStorePricingOption, setMerchantStorePricingOption] = useState([]);
  const [merchantStoreOption, setMerchantStoreOption] = useState([]);
  const [productOption, setProductOption] = useState([]);
  const [selectedMerchantStores, setSelectedMerchantStores] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ message: "", variant: "" });
  const [activeTab, setActiveTab] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const pricingOptions = await fetchMerchantStorePricings();
        await fetchMerchantStores(pricingOptions, true);
        await fetchProducts();
      } catch (error) {
        console.error("API error:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_NZ_PRICING_WALKTHROUGH !== "1") return;
    const storeName = process.env.REACT_APP_NZ_WALKTHROUGH_STORE_NAME;
    if (storeName && merchantStoreOption.length) {
      const store = merchantStoreOption.find((s) => s?.name === storeName);
      if (store?._id) {
        setSelectedMerchantStores([store._id]);
      }
    }
    const productNames = process.env.REACT_APP_NZ_WALKTHROUGH_PRODUCT_NAMES;
    if (productNames && productOption.length) {
      const wanted = productNames.split(",").map((s) => s.trim().toLowerCase());
      const ids = productOption
        .filter((p) => wanted.includes(String(p?.name || "").toLowerCase()))
        .map((p) => p._id);
      if (ids.length) setSelectedProducts(ids);
    }
  }, [merchantStoreOption, productOption]);

  const fetchMerchantStorePricings = async () => {
    try {
      const response = await api("v1/merchantStorePricing", "GET");
      if (response.data.success) {
        const raw = response.data.data.allMerchantStorePricings || [];
        const merchantStorePricingOptions = raw.filter(Boolean).filter(
          (option) =>
            option != null &&
            option.merchantStore != null &&
            option.product != null
        );
        setMerchantStorePricingOption(merchantStorePricingOptions);
        return merchantStorePricingOptions;
      }
    } catch (error) {
      console.error("API error:", error);
    }
    return [];
  };

  /**
   * @param {Array} pricingOptionsForDefault — snapshot from initial pricing fetch; only used when applyDefaultSelection is true
   * @param {boolean} applyDefaultSelection — if true, pre-select stores that have no pricing rows (initial load only)
   */
  const fetchMerchantStores = async (
    pricingOptionsForDefault = [],
    applyDefaultSelection = false
  ) => {
    try {
      // Admin pricing must list every active store, including locations with new signups disabled
      const response = await api("v1/merchantStore?includeSignupDisabled=true", "GET");
      if (response.data.success) {
        const raw = response.data.data.allMerchantStores || [];
        const merchantStoreOptions = raw.filter((store) => store != null && store._id != null);
        setMerchantStoreOption(merchantStoreOptions);

        if (applyDefaultSelection) {
          const storesWithoutPricing = merchantStoreOptions.filter(
            (store) =>
              !pricingOptionsForDefault.some(
                (option) => option?.merchantStore?._id === store._id
              )
          );
          setSelectedMerchantStores(storesWithoutPricing.map((store) => store._id));
        }
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      // Fetch all products including add-ons for admin portal
      const isAdmin = sessionStorage.getItem("selectedPortal") == "Administrator";
      const endpoint = isAdmin ? "v1/product?includeAll=true" : "v1/product";
      const response = await api(endpoint, "GET");
      if (response.data.success) {
        const raw = response.data.data.allProducts || [];
        const productOptions = raw.filter((product) => product != null && product._id != null);
        setProductOption(productOptions);
      }
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handlePricingChange = async (
    storeId,
    productId,
    price,
    adminFee,
    useVehicleVariantPricing,
    vehicleTypeVariants,
    {
      priceFieldEdited = false,
      variantsFieldEdited = false,
      useTierFieldEdited = false,
    } = {}
  ) => {
    setLoading(true);
    try {
      const existingRecordIndex = findPricingRowIndex(
        merchantStorePricingOption,
        storeId,
        productId
      );
      const existingRecord =
        existingRecordIndex !== -1
          ? merchantStorePricingOption[existingRecordIndex]
          : null;
      const adminFeeToSend =
        adminFee != null && adminFee !== "" && !Number.isNaN(Number(adminFee))
          ? adminFee
          : 8;
      const variantPayload = {
        sedan: vehicleTypeVariants?.sedan ?? null,
        wagon: vehicleTypeVariants?.wagon ?? null,
        "4wd": vehicleTypeVariants?.["4wd"] ?? null,
      };
      const dataToSend = {
        merchantStore: storeId,
        product: productId,
        adminFee: adminFeeToSend,
        priceFieldEdited: !!priceFieldEdited,
        variantsFieldEdited: !!variantsFieldEdited,
      };
      if (
        shouldSendVehicleTierFieldsInRequest({
          hasExistingRow: existingRecordIndex !== -1,
          variantsFieldEdited,
          useTierFieldEdited,
          vehicleTypeVariants: variantPayload,
        })
      ) {
        dataToSend.useVehicleVariantPricing = !!useVehicleVariantPricing;
        dataToSend.vehicleTypeVariants = variantPayload;
      }
      if (
        shouldSendPriceInRequest({
          hasExistingRow: existingRecordIndex !== -1,
          price,
          priceFieldEdited,
        })
      ) {
        dataToSend.price =
          !isNaN(parseFloat(price)) && price !== "" && price != null
            ? price
            : existingRecord?.price ?? "0";
      }

      const shouldPersist = shouldPersistPricingRow({
        price,
        vehicleTypeVariants: variantPayload,
        useVehicleVariantPricing: !!useVehicleVariantPricing,
        hasExistingRow: existingRecordIndex !== -1,
        adminFee: adminFeeToSend,
        variantsFieldEdited,
      });

      let response = null;
      if (shouldPersist) {
        if (existingRecordIndex !== -1) {
          // Update existing record
          const existingRecord = merchantStorePricingOption[existingRecordIndex];
          response = await api(`v1/merchantStorePricing/${existingRecord._id}`, "PATCH", dataToSend);

          // Existing Pricing Option updated, update the current options state with the new values
          const updated =
            response.data.data.updatedMerchantStorePricing ||
            response.data.data.merchantStorePricing;
          if (updated && updated._id) {
            setMerchantStorePricingOption((prevOptions) =>
              prevOptions.map((option) =>
                option?._id === updated._id ? updated : option
              )
            );
          } else {
            await fetchMerchantStorePricings();
          }
          setSnackbar({ variant: "success", message: `Pricing Option Updated. Price: ${price}, Admin Fee: ${adminFee} for ${storeId}` });

        } else {
          // Add new pricing option
          response = await api("v1/merchantStorePricing", "POST", dataToSend);

          // New pricing added, update the current options state with the new pricing added
          const created = response.data.data.newMerchantStorePricing;
          if (created && created._id) {
            setMerchantStorePricingOption((prevOptions) => [
              ...prevOptions.filter(Boolean),
              created,
            ]);
          } else {
            await fetchMerchantStorePricings();
          }
          console.log("SET NEW ENTRY", merchantStorePricingOption);
          setSnackbar({ variant: "success", message: `Pricing Option Created. Price: ${price}, Admin Fee: ${adminFee} for ${storeId}` });

        }
      }
    } catch (error) {
      console.error("API error:", error);
      setSnackbar({
        variant: "error",
        message: "Could not save store pricing. Please try again or refresh the page.",
      });
    }
    setLoading(false);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      // Fetch all data needed for export
      const allPricing = merchantStorePricingOption;
      const allStores = merchantStoreOption;
      const allProducts = productOption;
      
      // Build CSV with all pricing data
      const headers = [
        "Store Name",
        "Store ID",
        "Product Name",
        "Product Type",
        "Price",
        "Admin Fee (%)",
        "Use Vehicle Tiers",
        "Sedan/Hatch Price",
        "SUV/Wagon Price",
        "4WD/7Seater Price",
      ];
      
      const rows = [];
      
      // Create a map for faster lookups
      const storeMap = new Map(allStores.map(s => [s._id, s]));
      const productMap = new Map(allProducts.map(p => [p._id, p]));
      
      // For each store, create a row for each product (including those without pricing)
      allStores.forEach(store => {
        allProducts.forEach(product => {
          const pricingRow = allPricing.find(
            p => p.merchantStore?._id === store._id && p.product?._id === product._id
          );
          
          const price = pricingRow?.price ?? "";
          const adminFee = pricingRow?.adminFee ?? 8; // Default is 8%
          const useVehicleTiers = pricingRow?.useVehicleVariantPricing === true ? "Yes" : "No";
          const sedanPrice = pricingRow?.vehicleTypeVariants?.sedan ?? "";
          const wagonPrice = pricingRow?.vehicleTypeVariants?.wagon ?? "";
          const fourWdPrice = pricingRow?.vehicleTypeVariants?.["4wd"] ?? "";
          
          rows.push([
            store.name,
            store._id,
            product.name,
            product.type,
            price,
            adminFee,
            useVehicleTiers,
            sedanPrice,
            wagonPrice,
            fourWdPrice,
          ]);
        });
      });
      
      // Convert to CSV
      const csvContent = [
        headers.join(","),
        ...rows.map(row => 
          row.map(cell => {
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            const value = String(cell ?? "");
            if (value.includes(",") || value.includes('"') || value.includes("\n")) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(",")
        ),
      ].join("\n");
      
      // Download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "store-pricing-export.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      
      setSnackbar({ 
        variant: "success", 
        message: `Exported ${rows.length} pricing rows (${allStores.length} stores × ${allProducts.length} products)` 
      });
    } catch (error) {
      console.error("Export failed", error);
      setSnackbar({ 
        variant: "error", 
        message: "Export failed. Please try again." 
      });
    } finally {
      setExporting(false);
    }
  };

  const handleInputChange = (storeId, productId, field, value) => {
    setMerchantStorePricingOption((prevOptions) => {
      const existingRecordIndex = findPricingRowIndex(prevOptions, storeId, productId);
      if (existingRecordIndex === -1) {
        return [
          ...prevOptions.filter(Boolean),
          {
            merchantStore: { _id: storeId },
            product: { _id: productId },
            [field]: value,
          },
        ];
      }
      return prevOptions.filter(Boolean).map((option, index) =>
        index === existingRecordIndex ? { ...option, [field]: value } : option
      );
    });
  };

  const handleVariantInputChange = (storeId, productId, key, value) => {
    setMerchantStorePricingOption((prevOptions) => {
      const existingRecordIndex = findPricingRowIndex(prevOptions, storeId, productId);
      if (existingRecordIndex === -1) {
        return [
          ...prevOptions.filter(Boolean),
          {
            merchantStore: { _id: storeId },
            product: { _id: productId },
            vehicleTypeVariants: { [key]: value },
          },
        ];
      }
      return prevOptions.filter(Boolean).map((option, index) =>
        index === existingRecordIndex
          ? {
              ...option,
              vehicleTypeVariants: {
                ...(option.vehicleTypeVariants || {}),
                [key]: value,
              },
            }
          : option
      );
    });
  };

  const handleMerchantStoreSelectChange = (selected) => {
    if (selected.includes('all')) {
      if (selected.length === merchantStoreOption.length + 1) {
        // If all options including "ALL" are selected, deselect all
        setSelectedMerchantStores([]);
      } else {
        // Select all options including "ALL"
        setSelectedMerchantStores([
          "all",
          ...merchantStoreOption.map((store) => store?._id).filter(Boolean),
        ]);
      }
    } else {
      // Remove "ALL" if it's present and any individual option is deselected
      if (selected.length < merchantStoreOption.length) {
        setSelectedMerchantStores(selected.filter(option => option !== 'all'));
      } else {
        setSelectedMerchantStores(selected);
      }
    }
  };

  const filteredMerchantStores =
    selectedMerchantStores.length > 0
      ? merchantStoreOption.filter((store) =>
          store != null && selectedMerchantStores.includes(store._id)
        )
      : merchantStoreOption;

  // Filter products by category (guard against null entries)
  const subscriptions = productOption.filter(
    (product) => product != null && product.type === "subscription"
  );
  const oneTimeProducts = productOption.filter(
    (product) =>
      product != null && product.type === "onceoff" && !product.isAddOnOnly
  );
  const addOnProducts = productOption.filter(
    (product) => product != null && product.isAddOnOnly === true
  );

  // Get products for current tab
  const getProductsForTab = () => {
    if (activeTab === 0) {
      return subscriptions;
    } else if (activeTab === 1) {
      return oneTimeProducts;
    } else if (activeTab === 2) {
      return addOnProducts;
    }
    return productOption;
  };

  const productsForTab = getProductsForTab();

  /** Tier-capable products: show variant editor when tiers are stored or "Use tiers" is enabled. */
  const shouldShowVariantsInCell = (product, option) => {
    if (!product || !option || Object.keys(option).length === 0) return false;
    const tierCapable =
      product.type === "subscription" || product.isAddOnOnly === true;
    if (!tierCapable) return false;
    const hasStored = variantsObjectHasAny(option.vehicleTypeVariants);
    if (option.useVehicleVariantPricing === true || hasStored) return true;
    return false;
  };

  // Apply product filter selection
  const filteredProducts =
    selectedProducts.length > 0
      ? productsForTab.filter(
          (product) =>
            product != null && selectedProducts.includes(product._id)
        )
      : productsForTab;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Clear product selection when switching tabs
    setSelectedProducts([]);
  };

  const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
      <Typography
        component="div"
        role="tabpanel"
        hidden={value !== index}
        id={`pricing-tabpanel-${index}`}
        aria-labelledby={`pricing-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
      </Typography>
    );
  };

  return (
    <div className="page merchantStorePricing w-full flex flex-column items-center p-4 overflow-auto">
      {loading && <GlobalLoadingSpinner />}

      <div className="pageTitle flex flex-col w-full text-center lg:text-left mb-8 gap-3">
        <h2>Store Pricing Options</h2>
        <div className="pageActions flex flex-wrap items-center justify-center lg:justify-start gap-3">
          <Button 
            variant="outlined" 
            onClick={handleExport} 
            disabled={exporting || loading}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      <div className="filters mb-4 flex flex-wrap gap-4">
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="merchant-store-select-label">Merchant Store</InputLabel>
          <Select
            labelId="merchant-store-select-label"
            multiple
            value={selectedMerchantStores}
            onChange={(e) => handleMerchantStoreSelectChange(e.target.value)}
            renderValue={(selected) => {
              const selectedStores = selected.map((id) =>
                merchantStoreOption.find((store) => store?._id === id)?.name
              );
              if (selected.includes('all')) {
                return 'All';
              } else if (selectedStores.length <= 2) {
                return selectedStores.join(', ');
              } else {
                return selectedStores.slice(0, 2).join(', ') + ` +${selectedStores.length - 2}`;
              }
            }}
          >
            <MenuItem value="all">
              <Checkbox checked={selectedMerchantStores.includes('all')} />
              <ListItemText primary="ALL" />
            </MenuItem>
            {merchantStoreOption
              .filter((store) => store != null)
              .map((store) => (
                <MenuItem key={store._id} value={store._id}>
                  <Checkbox checked={selectedMerchantStores.includes(store._id)} />
                  <ListItemText primary={store.name} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="product-select-label">Product</InputLabel>
          <Select
            labelId="product-select-label"
            multiple
            value={selectedProducts}
            onChange={(e) => setSelectedProducts(e.target.value)}
            renderValue={(selected) =>
              selected
                .map((id) =>
                  productsForTab.find((product) => product?._id === id)?.name
                )
                .join(", ")
            }
          >
            {productsForTab
              .filter((product) => product != null)
              .map((product) => (
                <MenuItem key={product._id} value={product._id}>
                  <Checkbox checked={selectedProducts.indexOf(product._id) > -1} />
                  <ListItemText primary={product.name} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </div>

      {sessionStorage.getItem("selectedPortal") == "Administrator" && (
        <Box sx={{ width: "100%", borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="pricing tabs">
            <Tab label={`Subscriptions (${subscriptions.length})`} />
            <Tab label={`One-Time Products (${oneTimeProducts.length})`} />
            <Tab label={`Add-On Products (${addOnProducts.length})`} />
          </Tabs>
        </Box>
      )}

      <div className="table-container w-full">
        <table className="store-pricing-table w-full">
          <thead>
            <tr className="flex w-full flex-nowrap border">
              <th className="min-w-[400px] border p-1">Merchant Store</th>
              {filteredProducts.map((product) => (
                <th className="border p-1 w-[200px]" key={product._id}>{product.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredMerchantStores.map((store) => (
              <tr key={store._id} className="flex w-full flex-nowrap border-bottom">
                <td className="w-[400px] border-x p-4">{store.name}</td>
                {filteredProducts.map((product) => {
                  const pricingRowIndex = findPricingRowIndex(
                    merchantStorePricingOption,
                    store._id,
                    product._id
                  );
                  const currentOption =
                    pricingRowIndex === -1
                      ? {}
                      : merchantStorePricingOption[pricingRowIndex];
                  return (
                    <td className="border w-[200px]" key={product._id}>
                      <div className="flex flex-column justify-center items-center gap-2">
                        <div className="flex flex-column divide-y">
                          <div className="flex gap-2 justify-between items-center">
                            <label>Price</label>
                            <input
                              className="max-w-[100px]"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Price"
                              value={currentOption.price || 0} // Default price to 0
                              onChange={(e) => {
                                const price = parseFloat(e.target.value);
                                handleInputChange(store._id, product._id, 'price', price);
                                handlePricingChange(
                                  store._id,
                                  product._id,
                                  price,
                                  currentOption.adminFee || 8,
                                  currentOption?.useVehicleVariantPricing === true,
                                  currentOption?.vehicleTypeVariants || {},
                                  { priceFieldEdited: true }
                                );
                              }}
                            />
                          </div>
                          <div className="flex gap-2 justify-between items-center">
                            <label>Admin Fee</label>
                            <input
                              className="max-w-[100px]"
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="Admin Fee (%)"
                              value={currentOption.adminFee ?? 8}
                              onChange={(e) => {
                                const adminFee = parseFloat(e.target.value);
                                handleInputChange(store._id, product._id, "adminFee", e.target.value);
                                if (Number.isNaN(adminFee)) return;
                                handlePricingChange(
                                  store._id,
                                  product._id,
                                  currentOption.price ?? 0,
                                  adminFee,
                                  currentOption?.useVehicleVariantPricing === true,
                                  currentOption?.vehicleTypeVariants || {},
                                  {
                                    priceFieldEdited: false,
                                    variantsFieldEdited: false,
                                    useTierFieldEdited: false,
                                  }
                                );
                              }}
                            />
                          </div>
                        </div>
                        {shouldShowVariantsInCell(product, currentOption) && (
                          <div className="w-full border-t pt-2 text-[12px]">
                            <div className="font-semibold mb-1">Variants</div>
                            <div className="flex justify-between items-center mb-2">
                              <span>Use tiers</span>
                              <input
                                type="checkbox"
                                checked={currentOption?.useVehicleVariantPricing === true}
                                onChange={(e) => {
                                  const checked = !!e.target.checked;
                                  handleInputChange(
                                    store._id,
                                    product._id,
                                    "useVehicleVariantPricing",
                                    checked
                                  );
                                  handlePricingChange(
                                    store._id,
                                    product._id,
                                    currentOption.price || 0,
                                    currentOption.adminFee || 8,
                                    checked,
                                    currentOption.vehicleTypeVariants || {},
                                    {
                                      priceFieldEdited: false,
                                      variantsFieldEdited: false,
                                      useTierFieldEdited: true,
                                    }
                                  );
                                }}
                              />
                            </div>
                            {product?.supportsVehicleVariantPricing !== true && (
                              <div className="text-amber-700 mb-1">
                                Catalogue vehicle pricing flag off — store tier amounts still apply when
                                &quot;Use tiers&quot; is on or base price is $0.
                              </div>
                            )}
                            <div className="flex gap-2 justify-between items-center">
                              <span>Sedan/Hatch</span>
                              <input
                                className="max-w-[100px]"
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={currentOption?.vehicleTypeVariants?.sedan ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleVariantInputChange(store._id, product._id, "sedan", value);
                                  const nextVariants = {
                                    ...(currentOption.vehicleTypeVariants || {}),
                                    sedan: value,
                                  };
                                  handlePricingChange(
                                    store._id,
                                    product._id,
                                    currentOption.price || 0,
                                    currentOption.adminFee || 8,
                                    currentOption?.useVehicleVariantPricing === true,
                                    nextVariants,
                                    {
                                      priceFieldEdited: false,
                                      variantsFieldEdited: true,
                                      useTierFieldEdited: false,
                                    }
                                  );
                                }}
                              />
                            </div>
                            <div className="flex gap-2 justify-between items-center">
                              <span>SUV/Wagon</span>
                              <input
                                className="max-w-[100px]"
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={currentOption?.vehicleTypeVariants?.wagon ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleVariantInputChange(store._id, product._id, "wagon", value);
                                  const nextVariants = {
                                    ...(currentOption.vehicleTypeVariants || {}),
                                    wagon: value,
                                  };
                                  handlePricingChange(
                                    store._id,
                                    product._id,
                                    currentOption.price || 0,
                                    currentOption.adminFee || 8,
                                    currentOption?.useVehicleVariantPricing === true,
                                    nextVariants,
                                    {
                                      priceFieldEdited: false,
                                      variantsFieldEdited: true,
                                      useTierFieldEdited: false,
                                    }
                                  );
                                }}
                              />
                            </div>
                            <div className="flex gap-2 justify-between items-center">
                              <span>4WD/7Seater</span>
                              <input
                                className="max-w-[100px]"
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                value={currentOption?.vehicleTypeVariants?.["4wd"] ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  handleVariantInputChange(store._id, product._id, "4wd", value);
                                  const nextVariants = {
                                    ...(currentOption.vehicleTypeVariants || {}),
                                    "4wd": value,
                                  };
                                  handlePricingChange(
                                    store._id,
                                    product._id,
                                    currentOption.price || 0,
                                    currentOption.adminFee || 8,
                                    currentOption?.useVehicleVariantPricing === true,
                                    nextVariants,
                                    {
                                      priceFieldEdited: false,
                                      variantsFieldEdited: true,
                                      useTierFieldEdited: false,
                                    }
                                  );
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {snackbar && <CustomSnackbar {...snackbar} />}
    </div>
  );
};

export default MerchantStorePricing;
